import { AfterViewInit, CUSTOM_ELEMENTS_SCHEMA, Component, ElementRef, OnChanges, OnDestroy, ViewChild, inject, input } from '@angular/core'
import { CommonModule } from '@angular/common'
import { IAsset } from 'src/app/core/interfaces/asset.interface'
import { v4 as uuidv4 } from 'uuid'
import { ImageService } from 'src/app/core/services/image.service'
import { ImageSourceDirective } from 'src/app/core/directives/image-source.directive'
import { ShareButtonComponent, ShareButtonLocation } from '../share-button/share-button.component'
import { IconButtonModule } from '../icon-button/icon-button.module'
import { LegacyImageProcessorTypes } from 'src/app/core/interfaces/image.interface'
import { GlobalDataService } from 'src/app/core/services/global-data.service'
import { ComparisonToggleComponent, ComparisonToggleLocation } from '../comparison-toggle/comparison-toggle.component'
import { ModalService } from 'src/app/core/services/modal.service'
import { debounceTime, Subject } from 'rxjs'

@Component({
  selector: 'app-details-carousel',
  template: `
  <div class="container asset-carousel__swipe-navigation" data-testid="details-carousel">
      <app-icon-button
        icon-btn-medium
        icon-btn-icon-large
        icon-btn-black
        class="asset-carousel__nav-item asset-carousel__nav-item--prev"
        [class.is-hidden]="!hasNavigationControls"
        [class]="carouselId + '-prev'"
        matIcon="navigate_before"
        data-testid="prev-button"
      ></app-icon-button>
      <app-icon-button
        icon-btn-medium
        icon-btn-icon-large
        icon-btn-black
        class="asset-carousel__nav-item asset-carousel__nav-item--next"
        [class.is-hidden]="!hasNavigationControls"
        [class]="carouselId + '-next'"
        matIcon="navigate_next"
        data-testid="next-button"
      ></app-icon-button>
    </div>
    <div class="asset-carousel__wrapper">
      <!-- <div class="asset-carousel__hint-text caption">Hover Image to Zoom</div> -->
      @if (showShareButton()) {
        <app-share-button [location]="ShareButtonLocation.Carousel" data-testid="share-button"></app-share-button>
      }
      <app-comparison-toggle
        [asset]="this.asset()"
        [location]="ComparisonToggleLocation.AssetCard"
      />
      <div class="container swiper-alignment-container">
        @for (i of rerenderProps; track i) {
          <swiper-container
            #carouselWrapper
            [class.carousel-disabled]="!hasNavigationControls"
            init="false"
            [asset]="asset"
          >
            @if (asset().images && asset().images.length) {
              @for (item of asset().images; track $index) {
                <swiper-slide class="asset-carousel__item" data-testid="swiper-slide" (click)="openGallery()">
                  <div class="asset-carousel__image">
                    <div class="asset-carousel__badges"></div>
                    <h3 class="asset-carousel__image-container">
                      <img
                        [imageSource]="{
                          guid: asset().images[$index],
                          transparent: false,
                          type: LegacyImageProcessorTypes.CarouselItem,
                        }"
                        [applyWatermark]="true"
                        [alt]="asset().year + ' ' + asset().make + ' ' + asset().model + ' image: #' + ($index + 1)"
                        data-testid="carousel-image"
                      />
                    </h3>
                  </div>
                </swiper-slide>
              }
            } @else {
              <swiper-slide class="asset-carousel__item">
                <div class="asset-carousel__image">
                  <img
                    [src]="globalData.defaultImage()"
                    alt="No photo available"
                    i18n-alt="@@general.no-image.alt"
                    data-testid="carousel-image"
                  />
                </div>
              </swiper-slide>
            }
          </swiper-container>
        }
      </div>
    </div>
  `,
  styleUrls: ['./details-carousel.component.scss'],
  imports: [
    CommonModule,
    ImageSourceDirective,
    ShareButtonComponent,
    IconButtonModule,
    ComparisonToggleComponent,
  ],
  // providers: [Gallery, Lightbox],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DetailsCarouselComponent implements AfterViewInit, OnChanges, OnDestroy {
  public rerenderProps: Array<number> = [1]
  protected imageService = inject(ImageService)
  protected globalData = inject(GlobalDataService)
  protected modalService = inject(ModalService)
  ShareButtonLocation = ShareButtonLocation
  ComparisonToggleLocation = ComparisonToggleLocation

  showShareButton = input(false)
  asset = input.required<IAsset>()
  watermarkLogo = input.required<string>()
  @ViewChild('carouselWrapper') carouselWrapper!: ElementRef

  LegacyImageProcessorTypes = LegacyImageProcessorTypes
  hasNavigationControls: boolean = false
  images: Array<{ src: string, thumb: string }> = []
  carouselId = `carousel-${uuidv4()}`
  reRenderProps = [1]

  initializationSubject: Subject<void> = new Subject()

  constructor() {
    this.initializationSubject.asObservable().pipe(
      debounceTime(100)
    ).subscribe(() => {
      this.galleryInit()
      this.swiperInit()
    })
  }

  ngAfterViewInit() {
    this.initializationSubject.next()
  }

  ngOnChanges() {
    this.rerenderProps[0]++
    this.initializationSubject.next()
  }

  ngOnDestroy(): void {
    this.initializationSubject.complete()
  }

  async galleryInit() {
    this.images = []
    for (const guid of (this.asset().images ?? [])) {
      let src = this.imageService.buildImageSrc(guid, { guid, type: LegacyImageProcessorTypes.CarouselItem, transparent: true })
      if (this.watermarkLogo()) {
        src = await this.imageService.applyWatermark(src, this.watermarkLogo())
      }
      this.images.push({
        src,
        thumb: this.imageService.buildImageSrc(guid, { guid, type: LegacyImageProcessorTypes.FeaturedThumb, transparent: true }),
      })
    }
  }

  openGallery() {
    this.modalService.gallery({
      title: `${this.asset().year} ${this.asset().make} ${this.asset().model}`,
      images: this.images,
    })
  }

  swiperInit() {
    this.hasNavigationControls = (this.asset().images ?? []).length > 2

    const carouselWrapperParams = {
      slidesPerView: 'auto',
      speed: 500,
      loop: (this.asset().images ?? []).length > 3,
      spaceBetween: 12,
      loadPrevNext: true,
      keyboard: {
        enabled: false,
      },
      navigation: {
        nextEl: `.${this.carouselId}-next button`,
        prevEl: `.${this.carouselId}-prev button`,
      },
      pagination: true,
    }
    Object.assign(this.carouselWrapper.nativeElement, carouselWrapperParams)
    this.carouselWrapper.nativeElement.initialize()
    if ((this.asset().images ?? []).length > 3) {
      setTimeout(() => {
        this.carouselWrapper.nativeElement.swiper.slidePrev(0)
        this.carouselWrapper.nativeElement.swiper.slideNext(0)
      }, 750)
    }
  }
}
